<script setup>
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store.js';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import PageLayout from 'dashboard/components-next/metaAgent/PageLayout.vue';
import StatusCard from 'dashboard/components-next/metaAgent/StatusCard.vue';

const FOLLOWUP_INTERVALS = [300, 900, 1800, 3600, 7200, 28800, 86400];

const { t } = useI18n();
const store = useStore();
const route = useRoute();

const agent = useMapGetter('metaAgent/getAgent');
const settings = useMapGetter('metaAgent/getSettings');
const allowlist = useMapGetter('metaAgent/getAllowlist');
const uiFlags = useMapGetter('metaAgent/getUIFlags');

const newNumber = ref('');
const form = reactive({
  rolloutEnabled: false,
  audience: 'ALLOWLISTED_ONLY',
  handoffEnabled: false,
  handoffMessage: '',
  followupEnabled: false,
  followupInterval: 3600,
  followupMessage: '',
});

const inboxId = computed(() => Number(route.params.inboxId));
const isOnboarded = computed(
  () => Boolean(agent.value.id) && agent.value.lifecycle !== 'pending_setup'
);

const syncForm = () => {
  const remote = settings.value || {};
  form.rolloutEnabled = remote.rollout?.enabled ?? false;
  form.audience = remote.ai_audience || 'ALLOWLISTED_ONLY';
  form.handoffEnabled = remote.handoff?.enabled ?? false;
  form.handoffMessage = remote.handoff?.message || '';
  form.followupEnabled = remote.followup?.enabled ?? false;
  form.followupInterval = remote.followup?.followup_interval_in_seconds || 3600;
  form.followupMessage = remote.followup?.message || '';
};

// Meta's `detail` names the offending field; the generic error is the fallback.
const alertFailure = error => {
  const data = error?.response?.data || {};
  useAlert(data.detail || data.error || t('META_AGENT.SETTINGS.ERROR'));
};

// The form mirrors the store, not the outcome of load(). If a read fails after
// the agent is already cached, an imperative sync would be skipped and the form
// would render its defaults (rollout off) over a live agent — one Save away
// from silently switching it off.
watch(settings, syncForm, { immediate: true, deep: true });

const load = async () => {
  try {
    await store.dispatch('metaAgent/getInboxes');
    await store.dispatch('metaAgent/getAgent', inboxId.value);
    if (isOnboarded.value) {
      await store.dispatch('metaAgent/getAllowlist', inboxId.value);
    }
  } catch (error) {
    alertFailure(error);
  }
};

// Meta replaces settings wholesale, so every field goes on every save.
const buildPayload = () => ({
  rollout: { enabled: form.rolloutEnabled },
  handoff: { enabled: form.handoffEnabled, message: form.handoffMessage },
  followup: {
    enabled: form.followupEnabled,
    followup_interval_in_seconds: form.followupInterval,
    message: form.followupMessage,
  },
  ai_audience: form.audience,
});

const save = async () => {
  const goingPublic = form.rolloutEnabled && form.audience === 'EVERYONE';
  // eslint-disable-next-line no-alert
  if (
    goingPublic &&
    !window.confirm(t('META_AGENT.SETTINGS.AUDIENCE.CONFIRM_EVERYONE'))
  ) {
    return;
  }

  try {
    await store.dispatch('metaAgent/updateSettings', {
      inboxId: inboxId.value,
      settings: buildPayload(),
      confirm: goingPublic,
    });
    // No explicit sync: the watcher above re-reads the form from the readback.
    useAlert(t('META_AGENT.SETTINGS.SUCCESS'));
  } catch (error) {
    alertFailure(error);
  }
};

const addNumber = async () => {
  if (!newNumber.value) return;
  try {
    await store.dispatch('metaAgent/addAllowlistEntry', {
      inboxId: inboxId.value,
      consumerPhoneNumber: newNumber.value,
    });
    newNumber.value = '';
  } catch (error) {
    alertFailure(error);
  }
};

const removeNumber = async entryId => {
  try {
    await store.dispatch('metaAgent/removeAllowlistEntry', {
      inboxId: inboxId.value,
      entryId,
    });
  } catch (error) {
    alertFailure(error);
  }
};

onMounted(load);
watch(inboxId, load);
</script>

<template>
  <PageLayout
    :header-title="t('META_AGENT.SETTINGS.HEADER')"
    :is-fetching="uiFlags.fetchingAgent"
  >
    <div v-if="!isOnboarded" class="flex flex-col gap-4">
      <StatusCard
        variant="warning"
        :title="t('META_AGENT.OVERVIEW.ONBOARD.TITLE')"
        :description="t('META_AGENT.OVERVIEW.ONBOARD.DESCRIPTION')"
      />
    </div>
    <div v-else class="flex flex-col gap-4">
      <StatusCard
        :title="t('META_AGENT.SETTINGS.ROLLOUT.TITLE')"
        :description="t('META_AGENT.SETTINGS.ROLLOUT.DESCRIPTION')"
      >
        <input v-model="form.rolloutEnabled" type="checkbox" class="size-4" />
      </StatusCard>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('META_AGENT.SETTINGS.AUDIENCE.TITLE') }}
        </span>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input
            v-model="form.audience"
            type="radio"
            value="ALLOWLISTED_ONLY"
          />
          {{ t('META_AGENT.SETTINGS.AUDIENCE.ALLOWLISTED_ONLY') }}
        </label>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="form.audience" type="radio" value="EVERYONE" />
          {{ t('META_AGENT.SETTINGS.AUDIENCE.EVERYONE') }}
        </label>
      </div>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('META_AGENT.SETTINGS.ALLOWLIST.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('META_AGENT.SETTINGS.ALLOWLIST.DESCRIPTION') }}
        </span>
        <div class="flex items-center gap-2">
          <Input
            v-model="newNumber"
            :placeholder="t('META_AGENT.SETTINGS.ALLOWLIST.PLACEHOLDER')"
            class="flex-1"
          />
          <Button
            :label="t('META_AGENT.SETTINGS.ALLOWLIST.ADD')"
            size="sm"
            variant="faded"
            color="slate"
            @click="addNumber"
          />
        </div>
        <div
          v-for="entry in allowlist"
          :key="entry.id"
          class="flex items-center justify-between py-1 text-sm text-n-slate-12"
        >
          <span>{{ entry.consumer_phone_number }}</span>
          <Button
            :label="t('META_AGENT.SETTINGS.ALLOWLIST.REMOVE')"
            size="xs"
            variant="link"
            color="ruby"
            @click="removeNumber(entry.id)"
          />
        </div>
        <span v-if="!allowlist.length" class="text-sm text-n-slate-10">
          {{ t('META_AGENT.SETTINGS.ALLOWLIST.EMPTY') }}
        </span>
      </div>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('META_AGENT.SETTINGS.HANDOFF.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('META_AGENT.SETTINGS.HANDOFF.DESCRIPTION') }}
        </span>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="form.handoffEnabled" type="checkbox" />
          {{ t('META_AGENT.SETTINGS.HANDOFF.ENABLED') }}
        </label>
        <Input
          v-if="form.handoffEnabled"
          v-model="form.handoffMessage"
          :placeholder="t('META_AGENT.SETTINGS.HANDOFF.MESSAGE_PLACEHOLDER')"
        />
      </div>

      <div
        class="flex flex-col gap-3 p-4 border rounded-xl bg-n-solid-1 border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('META_AGENT.SETTINGS.FOLLOWUP.TITLE') }}
        </span>
        <span class="text-sm text-n-slate-11">
          {{ t('META_AGENT.SETTINGS.FOLLOWUP.DESCRIPTION') }}
        </span>
        <label class="flex items-center gap-2 text-sm text-n-slate-11">
          <input v-model="form.followupEnabled" type="checkbox" />
          {{ t('META_AGENT.SETTINGS.FOLLOWUP.ENABLED') }}
        </label>
        <template v-if="form.followupEnabled">
          <select v-model.number="form.followupInterval" class="w-48 !mb-0">
            <option
              v-for="interval in FOLLOWUP_INTERVALS"
              :key="interval"
              :value="interval"
            >
              {{ t(`META_AGENT.SETTINGS.FOLLOWUP.INTERVALS.${interval}`) }}
            </option>
          </select>
          <Input
            v-model="form.followupMessage"
            :placeholder="t('META_AGENT.SETTINGS.FOLLOWUP.MESSAGE_PLACEHOLDER')"
          />
        </template>
      </div>

      <div class="flex justify-end">
        <Button
          :label="t('META_AGENT.SETTINGS.SAVE')"
          size="sm"
          :is-loading="uiFlags.updatingSettings"
          @click="save"
        />
      </div>
    </div>
  </PageLayout>
</template>
